import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

const CANADIAN_TIMEZONES = [
  { value: 'America/Toronto', label: 'Eastern (Toronto / Ottawa)' },
  { value: 'America/Montreal', label: 'Eastern (Montreal)' },
  { value: 'America/Halifax', label: 'Atlantic (Halifax)' },
  { value: 'America/St_Johns', label: 'Newfoundland (St. John’s)' },
  { value: 'America/Winnipeg', label: 'Central (Winnipeg)' },
  { value: 'America/Regina', label: 'Central (Regina, no DST)' },
  { value: 'America/Edmonton', label: 'Mountain (Edmonton / Calgary)' },
  { value: 'America/Vancouver', label: 'Pacific (Vancouver)' },
  { value: 'America/Whitehorse', label: 'Yukon (Whitehorse)' },
  { value: 'America/Yellowknife', label: 'Mountain (Yellowknife)' },
  { value: 'America/Iqaluit', label: 'Eastern (Iqaluit)' },
];

export default function SignUp() {
  const navigate = useNavigate();

  const [orgLegalName, setOrgLegalName] = useState('');
  const [orgDisplayName, setOrgDisplayName] = useState('');
  const [timezone, setTimezone] = useState('America/Toronto');

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedDpa, setAcceptedDpa] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const DPA_VERSION = '2026.05.06';
  const MARKETING_CONSENT_TEXT =
    'Send me product updates, feature announcements, and tips for getting the most out of PharmaSync. I can unsubscribe at any time.';

  function validate(): string | null {
    if (!orgLegalName.trim()) return 'Please enter the organization’s legal name.';
    if (!orgDisplayName.trim()) return 'Please enter the organization’s display name.';
    if (!displayName.trim()) return 'Please enter your full name.';
    if (!email.trim()) return 'Please enter your email address.';
    if (!password) return 'Please enter a password.';
    if (password.length < 12) return 'Password must be at least 12 characters.';
    if (!/[a-z]/.test(password) || !/[A-Z]/.test(password) || !/\d/.test(password))
      return 'Password must include a lowercase letter, an uppercase letter, and a digit.';
    if (password !== passwordConfirm)
      return 'Passwords do not match. Please re-type the same password in both fields.';
    if (!acceptedTerms)
      return 'You must accept the Terms of Service and Privacy Notice to continue.';
    if (!acceptedDpa)
      return 'You must accept the Data Processing Agreement to continue.';
    return null;
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    const v = validate();
    if (v) {
      setError(v);
      return;
    }

    setSubmitting(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          data: {
            display_name: displayName.trim(),
            organization_legal_name: orgLegalName.trim(),
            organization_display_name: orgDisplayName.trim(),
            timezone,
            dpa_version: DPA_VERSION,
            marketing_consent: marketingConsent,
            marketing_consent_text: MARKETING_CONSENT_TEXT,
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      // Enumeration-safe error handling. Some signUp() failure modes
      // would otherwise leak whether an email is already registered:
      //   - Supabase "User already registered" error string
      //   - data.user with empty `identities` array (the privacy-preserving
      //     signal Supabase returns when the email exists and obscure-email
      //     is enabled in Auth settings)
      // In both cases we behave the same as a successful signup: navigate
      // to /auth/check-email. The CheckEmail copy is worded so it works
      // for both new and already-registered emails.
      if (error) {
        const msg = error.message.toLowerCase();
        const isAlreadyRegistered =
          msg.includes('already registered') ||
          msg.includes('already exists') ||
          msg.includes('user already');
        if (!isAlreadyRegistered) {
          setError(error.message);
          return;
        }
      }
      navigate('/auth/check-email', {
        replace: true,
        state: {
          email: email.trim(),
          alreadyRegistered:
            !!error || (data?.user?.identities?.length === 0),
        },
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-lg space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight">Create your PharmaSync account</h1>
          <p className="text-sm text-muted-foreground">Set up your organization and become its first owner.</p>
          <p className="text-xs text-muted-foreground rounded-md bg-muted/40 px-3 py-2">
            <strong>One organization can own one or many pharmacies.</strong>{' '}
            After verifying your email, you’ll add your first pharmacy as a <em>location</em> inside the app.
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-6">
          <fieldset className="space-y-4 rounded-md border border-border p-5">
            <legend className="px-2 text-sm font-medium">About your organization</legend>
            <p className="-mt-1 text-xs text-muted-foreground">
              The legal entity that owns one or more pharmacies on PharmaSync.
            </p>

            <Field label="Organization legal name" htmlFor="org-legal-name" hint="The official registered name on your business filing.">
              <input id="org-legal-name" type="text" autoComplete="organization" required value={orgLegalName} onChange={(e) => setOrgLegalName(e.target.value)} placeholder="e.g. Smith Pharmacy Group Inc." className={inputClass} />
            </Field>

            <Field label="Organization display name" htmlFor="org-display-name" hint="How the organization shows up in the app.">
              <input id="org-display-name" type="text" required value={orgDisplayName} onChange={(e) => setOrgDisplayName(e.target.value)} placeholder="e.g. Smith Pharmacy Group" className={inputClass} />
            </Field>

            <Field label="Default timezone" htmlFor="timezone" hint="Used as the starting point when you create a new location.">
              <select id="timezone" value={timezone} onChange={(e) => setTimezone(e.target.value)} className={inputClass}>
                {CANADIAN_TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </select>
            </Field>
          </fieldset>

          <fieldset className="space-y-4 rounded-md border border-border p-5">
            <legend className="px-2 text-sm font-medium">About you (the first owner)</legend>
            <p className="-mt-1 text-xs text-muted-foreground">
              You’ll be the first owner of this organization. Owners have full administrative control.
            </p>

            <Field label="Your full name" htmlFor="display-name" hint="Shown on schedules, audit records, and notifications.">
              <input id="display-name" type="text" autoComplete="name" required value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="e.g. Jane Smith" className={inputClass} />
            </Field>

            <Field label="Your email" htmlFor="email" hint="Used for sign-in, important notifications, and account recovery.">
              <input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@yourorg.ca" className={inputClass} />
            </Field>

            <Field label="Password" htmlFor="password" hint="At least 12 characters with upper, lower, and a digit. Click Show to reveal what you typed.">
              <div className="relative">
                <input id="password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" required minLength={12} value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass + ' pr-20'} />
                <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute inset-y-0 right-0 px-3 text-xs font-medium text-muted-foreground hover:text-foreground" aria-label={showPassword ? 'Hide password' : 'Show password'} aria-pressed={showPassword} tabIndex={-1}>
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </Field>

            <Field label="Confirm password" htmlFor="password-confirm" hint="Re-type your password to catch any typos.">
              <div className="relative">
                <input id="password-confirm" type={showPassword ? 'text' : 'password'} autoComplete="new-password" required minLength={12} value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} className={inputClass + ' pr-20' + (passwordConfirm && password !== passwordConfirm ? ' border-destructive focus-visible:ring-destructive' : '')} />
                <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute inset-y-0 right-0 px-3 text-xs font-medium text-muted-foreground hover:text-foreground" aria-label={showPassword ? 'Hide password' : 'Show password'} aria-pressed={showPassword} tabIndex={-1}>
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              {passwordConfirm && password !== passwordConfirm && (
                <p className="text-xs text-destructive" role="alert">Passwords don’t match yet.</p>
              )}
            </Field>
          </fieldset>

          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} className="mt-1 h-4 w-4 rounded border border-border" />
            <span>
              I have read and agree to the{' '}
              <a href="/terms" className="underline" target="_blank" rel="noreferrer">Terms of Service</a>{' '}
              and{' '}
              <a href="/privacy" className="underline" target="_blank" rel="noreferrer">Privacy Notice</a>.
            </span>
          </label>

          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={acceptedDpa} onChange={(e) => setAcceptedDpa(e.target.checked)} className="mt-1 h-4 w-4 rounded border border-border" />
            <span>
              On behalf of my organization, I accept the{' '}
              <a href="/dpa" className="underline" target="_blank" rel="noreferrer">Data Processing Agreement</a>{' '}
              ({DPA_VERSION}). This acceptance is recorded in our audit log.
            </span>
          </label>

          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={marketingConsent} onChange={(e) => setMarketingConsent(e.target.checked)} className="mt-1 h-4 w-4 rounded border border-border" />
            <span className="text-muted-foreground">{MARKETING_CONSENT_TEXT}</span>
          </label>

          {error && (
            <p role="alert" className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </p>
          )}

          <button type="submit" disabled={submitting} className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50">
            {submitting ? 'Creating account...' : 'Create organization and owner account'}
          </button>

          <p className="text-center text-xs text-muted-foreground">
            Next: verify your email, sign in, then add your first pharmacy as a location.
          </p>
        </form>

        <p className="text-center text-sm text-muted-foreground">
          Already have an account?{' '}
          <Link to="/auth/sign-in" className="underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}

function Field({ label, htmlFor, hint, children }: { label: string; htmlFor: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label htmlFor={htmlFor} className="block text-sm font-medium">{label}</label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

const inputClass =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';
